<script setup>
import { ref, computed } from 'vue';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { useMessageContext } from '../provider.js';

const { contentAttributes, attachments, createdAt, sender } =
  useMessageContext();

const showGallery = ref(false);
const selectedImageIndex = ref(0);
const hasError = ref({});

const images = computed(() => {
  return contentAttributes.value?.items || [];
});

const gridClass = computed(() => {
  const count = images.value.length;
  if (count === 1) return 'grid grid-cols-1 gap-1';
  if (count === 2) return 'grid grid-cols-2 gap-1';

  // For 3+ images: large image on left, smaller ones stacked on right
  return 'grid grid-cols-2 gap-1 grid-rows-2';
});

const getImageClass = index => {
  const count = images.value.length;

  if (count <= 2) return 'rounded-md';

  // For 3+ images: first image is large, others are smaller on the right
  if (index === 0) {
    return 'rounded-md row-span-2 h-[400px]';
  }
  return 'rounded-md h-[196px]';
};

// Convert image_set items to attachment-like objects for GalleryView
const imageAttachments = computed(() => {
  return images.value.map((image, index) => ({
    id: index,
    message_id: index,
    file_type: 'image',
    data_url: image.src,
    thumb_url: image.src,
    extension: null,
    created_at: createdAt.value,
    sender: sender.value,
  }));
});

const selectedAttachment = computed(() => {
  return (
    imageAttachments.value[selectedImageIndex.value] ||
    imageAttachments.value[0]
  );
});

const openImage = index => {
  selectedImageIndex.value = index;
  showGallery.value = true;
};

const handleError = index => {
  hasError.value[index] = true;
};

const closeGallery = () => {
  showGallery.value = false;
};
</script>

<template>
  <BaseBubble class="overflow-hidden p-1" data-bubble-name="image-set">
    <div :class="gridClass">
      <div
        v-for="(image, index) in images"
        :key="index"
        class="relative group overflow-hidden cursor-pointer"
        :class="getImageClass(index)"
        @click="openImage(index)"
      >
        <div
          v-if="hasError[index]"
          class="flex items-center justify-center gap-1 text-center rounded-lg bg-n-alpha-1 h-full"
        >
          <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
          <p class="mb-0 text-n-slate-11 text-sm">
            {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
          </p>
        </div>
        <img
          v-else
          class="skip-context-menu w-full h-full object-cover"
          :src="image.src"
          :alt="image.alt || image.title || ''"
          @error="handleError(index)"
        />
        <div
          class="inset-0 p-2 pointer-events-none absolute bg-gradient-to-tl from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent hidden group-hover:flex"
        />
        <div class="absolute right-2 bottom-2 hidden group-hover:flex gap-2">
          <Button xs solid slate icon="i-lucide-expand" class="opacity-60" />
        </div>
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery && selectedAttachment"
    v-model:show="showGallery"
    :attachment="selectedAttachment"
    :all-attachments="imageAttachments"
    @close="closeGallery"
  />
</template>
